// useAuth.jsx



// useAuth.jsx
import { useState, useEffect, useContext, createContext } from 'react';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signOut as firebaseSignOut,
  setPersistence,
  browserLocalPersistence,
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import app, { db as firestore } from '../firebase';

const auth = getAuth(app);

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const ROLES = {
  RENTER: 'renter',
  AGENT: 'agent',
  MANAGER: 'manager',
  // legacy alias we’ll normalize:
  LANDLORD_LEGACY: 'landlord',
};

const normalizeUserType = (raw) => {
  if (!raw) return null;
  if (raw === ROLES.LANDLORD_LEGACY) return ROLES.AGENT; // keep legacy users working
  return raw;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    const storedUser = localStorage.getItem('user');
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  const register = async (email, password, userType, additionalData = {}) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    console.log(`[AUTH] Registering user: ${email} as ${userType}`);
    await setDoc(doc(firestore, 'users', user.uid), {
      email: user.email,
      userType: userType || ROLES.RENTER, // Ensure userType is always set
      ...additionalData,
      emailVerified: false,
      createdAt: new Date().toISOString(),
    });
    console.log(`[AUTH] User document created in Firestore for UID: ${user.uid}`);


    // Custom action code settings for better email deliverability
    const actionCodeSettings = {
      url: 'https://tenantguidedtours.com/verify-email',
      handleCodeInApp: false,
    };

    await sendEmailVerification(user, actionCodeSettings);
    return user;
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      alert('Password reset email sent. Please check your inbox.');
      return { success: true };
    } catch (error) {
      console.error('Error sending password reset email:', error);
      alert('Failed to send password reset email. Please try again.');
      return { success: false, error };
    }
  };

  const fetchUserType = async (uid) => {
    const userDoc = await getDoc(doc(firestore, 'users', uid));
    const userType = userDoc.exists() ? userDoc.data().userType : null;
    return userType;
  };



  const login = async (email, password) => {
    // Persistence is set in the effect below; this just signs in.
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    const rawType = await fetchUserType(user.uid);
    const userType = normalizeUserType(rawType);
    console.log(`[AUTH] Login success: ${email}, UID: ${user.uid}, Type: ${userType}`);

    const loggedInUser = { uid: user.uid, email: user.email, userType };
    localStorage.setItem('user', JSON.stringify(loggedInUser));
    setCurrentUser(loggedInUser);

    return loggedInUser;

  };

  const logout = async () => {
    await firebaseSignOut(auth);
    localStorage.removeItem('user');
    setCurrentUser(null);
  };

  const checkEmailVerified = async (user) => {
    if (!user) return false;


    const current = auth.currentUser;
    if (current) {
      await current.reload();
      return current.emailVerified;
    }
    return false;
  };


  useEffect(() => {
  let unsubscribe = () => {};
  let active = true; 
  (async () => {
    try {
      await setPersistence(auth, browserLocalPersistence);
    } catch (e) {
      console.error('Failed to set auth persistence, proceeding with default.', e);
    } finally {
      unsubscribe = onAuthStateChanged(auth, async (user) => {
        if (!active) return; 
        try {
          if (user) {
            const rawType = await fetchUserType(user.uid);
            if (!active) return; 
            const userType = normalizeUserType(rawType);
            const loggedInUser = { uid: user.uid, email: user.email, userType };
            localStorage.setItem('user', JSON.stringify(loggedInUser));
            setCurrentUser(loggedInUser);
          } else {
            localStorage.removeItem('user');
            setCurrentUser(null);
          }
        } finally {
          if (active) setLoading(false);
        }
      });
    }
  })();

  return () => {
    active = false;
    unsubscribe();
  };
}, []);

  const value = {
    currentUser,
    register,
    login,
    logout,
    fetchUserType,
    checkEmailVerified,
    resetPassword,
    ROLES,
  };

  // Optionally show a splash/loader while determining auth state
  return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
};



// // useAuth.jsx
// import { useState, useEffect, useContext, createContext } from 'react';
// import {
//   getAuth,
//   createUserWithEmailAndPassword,
//   signInWithEmailAndPassword,
//   onAuthStateChanged,
//   sendEmailVerification,
//   sendPasswordResetEmail,
//   signOut as firebaseSignOut,
// } from 'firebase/auth';
// import { getFirestore, doc, setDoc, getDoc } from 'firebase/firestore';
// import app from '../firebase';

// const auth = getAuth(app);
// const firestore = getFirestore(app);

// const AuthContext = createContext();

// export const useAuth = () => useContext(AuthContext);

// export const ROLES = {
//   RENTER: 'renter',
//   AGENT: 'agent',
//   MANAGER: 'manager',
//   // legacy alias we’ll normalize:
//   LANDLORD_LEGACY: 'landlord',
// };

// const normalizeUserType = (raw) => {
//   if (!raw) return null;
//   if (raw === ROLES.LANDLORD_LEGACY) return ROLES.AGENT; // keep legacy users working
//   return raw;
// };

// export const AuthProvider = ({ children }) => {
//   const [currentUser, setCurrentUser] = useState(() => {
//     const storedUser = localStorage.getItem('user');
//     return storedUser ? JSON.parse(storedUser) : null;
//   });
//   const [loading, setLoading] = useState(true);

//   const register = async (email, password, userType, additionalData = {}) => {
//     const userCredential = await createUserWithEmailAndPassword(auth, email, password);
//     const user = userCredential.user;

//     await setDoc(doc(firestore, 'users', user.uid), {
//       email: user.email,
//       userType, // store what was chosen (manager supported)
//       ...additionalData,
//       emailVerified: false,
//       createdAt: new Date().toISOString(),
//     });

//     await sendEmailVerification(user);
//     return user;
//   };

//   const resetPassword = async (email) => {
//     try {
//       await sendPasswordResetEmail(auth, email);
//       alert('Password reset email sent. Please check your inbox.');
//       return { success: true };
//     } catch (error) {
//       console.error('Error sending password reset email:', error);
//       alert('Failed to send password reset email. Please try again.');
//       return { success: false, error };
//     }
//   };

//   const fetchUserType = async (uid) => {
//     const userDoc = await getDoc(doc(firestore, 'users', uid));
//     return userDoc.exists() ? userDoc.data().userType : null;
//   };

//   const login = async (email, password) => {
//     const userCredential = await signInWithEmailAndPassword(auth, email, password);
//     const user = userCredential.user;

//     const rawType = await fetchUserType(user.uid);
//     const userType = normalizeUserType(rawType);

//     const loggedInUser = { uid: user.uid, email: user.email, userType };
//     localStorage.setItem('user', JSON.stringify(loggedInUser));
//     setCurrentUser(loggedInUser);

//     return loggedInUser;
//   };

//   const logout = async () => {
//     await firebaseSignOut(auth);
//     localStorage.removeItem('user');
//     setCurrentUser(null);
//   };

//   const checkEmailVerified = async (user) => {
//     if (!user) return false;
//     const current = auth.currentUser;
//     if (current) {
//       await current.reload();
//       return current.emailVerified;
//     }
//     return false;
//   };

//   useEffect(() => {
//     const unsubscribe = onAuthStateChanged(auth, async (user) => {
//       try {
//         if (user) {
//           const rawType = await fetchUserType(user.uid);
//           const userType = normalizeUserType(rawType);
//           const loggedInUser = { uid: user.uid, email: user.email, userType };
//           localStorage.setItem('user', JSON.stringify(loggedInUser));
//           setCurrentUser(loggedInUser);
//         } else {
//           localStorage.removeItem('user');
//           setCurrentUser(null);
//         }
//       } finally {
//         setLoading(false);
//       }
//     });
//     return unsubscribe;
//   }, []);

//   const value = {
//     currentUser,
//     register,
//     login,
//     logout,
//     fetchUserType,
//     checkEmailVerified,
//     resetPassword,
//     ROLES,
//   };

//   return <AuthContext.Provider value={value}>{!loading && children}</AuthContext.Provider>;
// };


